import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CircularProgress, Box } from "@mui/material";
import { api } from "../api";
import { gold } from "../daiService";
import { toDashboardView, STATUS_CHIP_LABEL, type DashboardView, type SlaStatus } from "./daiAdapters";
import { STATUS_OPTIONS, marketOptions } from "../constants";
import DaiCustomSelectDropdown from "../components/DaiCustomSelectDropdown";
import DialogModal from "../components/DialogModalDai";
import StatusChip from "../components/StatusChip";
import { SummaryCard } from "../../Common/SummaryCards";
import Header from "../components/Header";
import Nav from "../components/Nav";
import Pagination from "../components/Pagination";
import GoldJobDetail from "../components/GoldJobDetail";
import GoldHourlyView from "../components/GoldHourlyView";
import DateInput from "../components/DateInput";
import { sortItems, useSortState, SortTh, useColumnVisibility, ColumnMenu, type SortDir } from "../hooks/tableTools";

function todayIso() { return new Date().toISOString().slice(0, 10); }
function daysAgoIso(n: number) { return new Date(Date.now() - n * 86400000).toISOString().slice(0, 10); }

const GOLD_COMPLETED_STATUSES = ["success", "delayed", "missed_sla"];
const GOLD_RUN_STATUS_MAP: Record<string, string> = {
  failed: "Failed", failure: "Failed", error: "Failed",
  cancelled: "Cancelled", canceled: "Cancelled",
  inprogress: "Running", running: "Running",
  success: "Completed", succeeded: "Completed", passed: "Completed",
};

function goldRunStatus(r: any): string {
  const raw = String(r.job_status || "").toLowerCase().replace(/[^a-z]/g, "");
  if (raw && GOLD_RUN_STATUS_MAP[raw]) return GOLD_RUN_STATUS_MAP[raw];
  if (raw.includes("fail") || raw.includes("error")) return "Failed";
  if (raw.includes("cancel")) return "Cancelled";
  if (raw.includes("progress") || raw.includes("running")) return "Running";
  if (raw.includes("success") || raw.includes("succeed") || raw.includes("pass")) return "Completed";
  if (r.actual_start && !r.actual_end) return "Running";
  if (r.actual_start && r.actual_end) return "Completed";
  return "Not Started";
}

function fmtDateTime(s?: string | null): string {
  if (!s) return "\u2014";
  const m = String(s).match(/(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2}:\d{2})/);
  return m ? `${m[1]} ${m[2]}` : String(s);
}

function fmtDur(mins?: number | null): string {
  if (mins == null) return "\u2014";
  const m = Math.round(mins);
  return m < 60 ? `${m}m` : `${Math.floor(m / 60)}h ${m % 60}m`;
}

interface GoldColumn {
  key: string;
  label: string;
  sortKey: string | null;
  render: (item: any, idx: number) => React.ReactNode;
}

// "#" is a frontend-only serial number (not sortable).
const COLUMNS: GoldColumn[] = [
  { key: "serial", label: "#", sortKey: null, render: (item, idx) => idx + 1 },
  { key: "market", label: "Market", sortKey: "market", render: (item) => item.market || "\u2014" },
  { key: "schema", label: "Schema", sortKey: "schema", render: (item) => <span style={{ fontSize: ".75rem", color: "#666" }}>{item.schema}</span> },
  { key: "job_name", label: "Job Name", sortKey: "job_name", render: (item) => <span className="job-name" title={item.job_name}>{item.job_name}</span> },
  { key: "display_start", label: "Plan Start", sortKey: "display_start", render: (item) => <span className="time-cell">{item.display_start || "\u2014"}</span> },
  { key: "display_end", label: "Plan End", sortKey: "display_end", render: (item) => <span className="time-cell">{item.display_end || "\u2014"}</span> },
  { key: "expected_start", label: "Expected Start", sortKey: "expected_start", render: (item) => <span className="time-cell">{item.expected_start || "\u2014"}</span> },
  { key: "sla_end", label: "Expected End", sortKey: "sla_end", render: (item) => <span className="time-cell">{item.sla_end || "\u2014"}</span> },
  { key: "actual_start", label: "Actual Start (UTC)", sortKey: "actual_start", render: (item) => <span className="datetime-cell">{fmtDateTime(item.actual_start)}</span> },
  { key: "actual_end", label: "Actual End (UTC)", sortKey: "actual_end", render: (item) => <span className="datetime-cell">{fmtDateTime(item.actual_end)}</span> },
  { key: "duration_mins", label: "Duration", sortKey: "duration_mins", render: (item) => <span className="dur-cell">{fmtDur(item.duration_mins)}</span> },
  { key: "run_count", label: "Runs", sortKey: "run_count", render: (item) => <span style={{ color: "#888" }}>{item.run_count || 0}</span> },
  { key: "trigger_type", label: "Trigger", sortKey: "trigger_type", render: (item) => <span style={{ fontSize: ".75rem", color: "#888" }}>{item.trigger_type || ""}</span> },
  {
    key: "status", label: "SLA Status", sortKey: "status", render: (item) => (
      <>
        <StatusChip status={STATUS_CHIP_LABEL[item.status as SlaStatus] || item.status} />
        {item.error_message && <span className="err-icon" title={item.error_message}>&#9888;</span>}
      </>
    ),
  },
  { key: "status_detail", label: "Job Run Status", sortKey: "status_detail", render: (item) => <span title={item.job_status || item.status_detail || ""}>{goldRunStatus(item)}</span> },
];
const COLUMN_KEYS = COLUMNS.map((c) => c.key);

const REPORT_STATUS_COLOR: Record<string, string> = {
  success: "#1e8e3e", delayed: "#c96a11", missed_sla: "#c96a11",
  missed: "#c0392b", failed: "#c0392b", inprog: "#2f7bc4", scheduled: "#64748b",
};

function GoldTableBody({
  view, pageItems, visibleColumns, sortKey, sortDir, onSort, startIndex, date, setOpenJob,
}: {
  view: DashboardView | null;
  pageItems: any[];
  visibleColumns: typeof COLUMNS;
  sortKey: string;
  sortDir: SortDir;
  onSort: (key: string) => void;
  startIndex: number;
  date: string;
  setOpenJob: (job: any) => void;
}) {
  if (!view) {
    return (
      <div id="loading-msg" className="loading" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, padding: "30px" }}>
        <CircularProgress size={24} />
        <span>Loading job status…</span>
      </div>
    );
  }
  if (pageItems.length === 0) {
    return <div id="no-data-msg" className="loading">No jobs found for this selection.</div>;
  }
  return (
    <table className="gold-table" id="gold-table">
      <thead>
        <tr>
          {visibleColumns.map((c) => (
            <SortTh key={c.key} columnKey={c.sortKey} sortKey={sortKey} sortDir={sortDir} onSort={onSort}>{c.label}</SortTh>
          ))}
        </tr>
      </thead>
      <tbody id="gold-tbody">
        {pageItems.map((it: any, idx: number) => (
          <tr key={idx} onClick={() => setOpenJob({ ...it, run_date: date })}>
            {visibleColumns.map((c) => <td key={c.key}>{c.render(it, startIndex + idx)}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ReportTableRows({ reportLoading, reportRows }: { reportLoading: boolean; reportRows: any[] }) {
  if (reportLoading) {
    return <tr><td colSpan={12} style={{ padding: 20, textAlign: "center", color: "#64748b" }}>Loading&hellip;</td></tr>;
  }
  if (reportRows.length === 0) {
    return <tr><td colSpan={12} style={{ padding: 20, textAlign: "center", color: "#64748b" }}>No data for this selection.</td></tr>;
  }
  return (
    <>
      {reportRows.map((r: any, i: number) => (
        <tr key={i} style={{ borderBottom: "1px solid #dde4ec" }}>
          <td style={{ padding: "6px 10px", color: "#475569" }}>{r.market || ""}</td>
          <td style={{ padding: "6px 10px", fontSize: ".7rem", color: "#94a3b8" }}>{r.schema_name || ""}</td>
          <td style={{ padding: "6px 10px", color: "#1f2d3d" }}>{r.job_name || ""}</td>
          <td style={{ padding: "6px 10px", color: "#475569" }}>{r.gdo_num || ""}</td>
          <td style={{ padding: "6px 10px", color: "#475569" }}>{r.report_start || "\u2014"}</td>
          <td style={{ padding: "6px 10px", color: "#475569" }}>{r.report_end || "\u2014"}</td>
          <td style={{ padding: "6px 10px", color: "#475569" }}>{fmtDateTime(r.actual_start)}</td>
          <td style={{ padding: "6px 10px", color: "#475569" }}>{fmtDateTime(r.actual_end)}</td>
          <td style={{ padding: "6px 10px", color: "#475569" }}>{r.actual_duration_mins != null ? fmtDur(r.actual_duration_mins) : "\u2014"}</td>
          <td style={{ padding: "6px 10px", fontWeight: 600, color: REPORT_STATUS_COLOR[r.computed_status] || "#64748b" }}>{r.status_detail || r.computed_status || "\u2014"}</td>
          <td style={{ padding: "6px 10px", fontSize: ".7rem", color: "#94a3b8" }}>{r.trigger_type || ""}</td>
          <td style={{ padding: "6px 10px", fontSize: ".7rem", color: "#c0392b", maxWidth: 200, overflow: "hidden" }}>{(r.error_message || "").slice(0, 80)}</td>
        </tr>
      ))}
    </>
  );
}

export default function GoldDashboard() {
  const [date, setDate] = useState(todayIso());
  const [market, setMarket] = useState("");
  const [markets, setMarkets] = useState<string[]>([]);
  const [view, setView] = useState<DashboardView | null>(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openJob, setOpenJob] = useState<any>(null);
  const [viewTab, setViewTab] = useState<"table" | "hourly">("table");
  const [reportOpen, setReportOpen] = useState(false);
  const [reportData, setReportData] = useState<any>(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState("");
  const { sortKey, sortDir, onSort, clearSort } = useSortState();
  const { hidden, toggle, selectAll, allChecked } = useColumnVisibility(COLUMN_KEYS);

  const fetchData = useCallback(async (d: string = date, m: string = market) => {
    const { ok, data, error: err } = await gold.getStatus(d, m || undefined);
    if (!ok) {
      setError("Failed to fetch data: " + err);
      return;
    }
    setView(toDashboardView((data as any)?.run_date ?? d, (data as any)?.items, { last_refresh: (data as any)?.pulled_at, db_live: (data as any)?.db_live }));
    setError("");
  }, [date, market]);

  // getMarkets() returns an unwrapped string[] — no { markets: [] } envelope.
  useEffect(() => {
    gold.getMarkets().then(({ ok, data }) => setMarkets(ok && Array.isArray(data) ? data : []));
  }, []);

  useEffect(() => {
    fetchData(date, market);
    const id = setInterval(() => fetchData(date, market), 60000);
    return () => clearInterval(id);
  }, [date, market, fetchData]);

  const filteredItems = useMemo(() => {
    const all: any[] = (view && view.items) || [];
    const term = search.toLowerCase();
    return all.filter((i) => {
      if (term && !(i.job_name || "").toLowerCase().includes(term)) return false;
      if (statusFilter === "completed") return GOLD_COMPLETED_STATUSES.includes(i.status);
      if (statusFilter && i.status !== statusFilter) return false;
      return true;
    });
  }, [view, search, statusFilter]);

  const sortedItems = useMemo(() => sortItems(filteredItems, sortKey, sortDir), [filteredItems, sortKey, sortDir]);

  useEffect(() => setPage(1), [search, statusFilter, market, date]);

  const pageItems = sortedItems.slice((page - 1) * rowsPerPage, page * rowsPerPage);
  const startIndex = (page - 1) * rowsPerPage;
  const summary = (view && view.summary) || {};
  const visibleColumns = COLUMNS.filter((c) => !hidden.has(c.key));
  const completedCount = (summary.success || 0) + (summary.delayed || 0) + (summary.missed_sla || 0);

  const toggleCardFilter = useCallback((status: string) => {
    setStatusFilter((cur) => (cur === status ? "" : status));
  }, []);

  const clearAllFilters = useCallback(() => {
    setSearch("");
    setStatusFilter("");
    setMarket("");
    setDate(todayIso());
  }, []);

  const openReport = useCallback(async () => {
    setReportOpen(true);
    setReportLoading(true);
    setReportError("");
    setReportData(null);
    const { ok, data, error: err } = await gold.getReport({ date, ...(market ? { market } : {}) });
    setReportLoading(false);
    if (!ok) {
      setReportError(err || "Failed to load report");
      return;
    }
    setReportData(data);
  }, [date, market]);

    const reportRows: any[] = (reportData && reportData.rows) || [];

  const exportCsv = useCallback(async () => {
    const { ok, data: csvText, error: err } = await api.goldReportExport({ date, ...(market ? { market } : {}) });
    if (!ok) { window.alert(err || "Export failed"); return; }
    const blob = new Blob([csvText], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `ibp_gold_report_${date}${market ? "_" + market : ""}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }, [date, market]);

  const subCards = [
    { status: "success", label: "On Time", count: summary.success || 0 },
    { status: "delayed", label: "Delayed Start", count: summary.delayed || 0 },
    { status: "missed_sla", label: "Missed SLA", count: summary.missed_sla || 0 },
  ];

  const renderTableView = () => (
    <div id="gold-tv-panel">
      <div className="controls">
        <input id="gold-search" type="text" placeholder="Search job name&hellip;" value={search} onChange={(e) => setSearch(e.target.value)} />
        <DaiCustomSelectDropdown options={STATUS_OPTIONS} value={statusFilter} handleChange={(e) => setStatusFilter(e.target.value)} noSelectionLabel="Select Status" />
        <DaiCustomSelectDropdown options={marketOptions(markets)} value={market} handleChange={(e) => setMarket(e.target.value)} noSelectionLabel="Select Market" />
        <DateInput id="date-picker" value={date} min={daysAgoIso(7)} max={todayIso()} onChange={(e) => setDate(e.target.value || todayIso())} />
        <div className="controls-actions">
          <span id="job-count-lbl" className="job-count">{summary.total ? `${summary.total} jobs for ${(view && view.day) || ""}` : ""}</span>
          <button className="btn-sm btn-report" onClick={openReport}>&#9660; Generate Report</button>
          <button className="btn-sm btn-export" onClick={exportCsv}>&#8659; Export CSV</button>
          <ColumnMenu columns={COLUMNS} hidden={hidden} toggle={toggle} selectAll={selectAll} allChecked={allChecked} />
          <button type="button" className="col-menu-btn" onClick={clearSort}>&#10005; Clear Sort</button>
          <button type="button" className="col-menu-btn" onClick={clearAllFilters}>&#10005; Clear Filters</button>
        </div>
      </div>
      {error && <div style={{ color: "#c0392b", padding: "6px 20px" }}>{error}</div>}
      <div className="gold-table-wrap">
        {!view ? (
          <div id="loading-msg" className="loading" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, padding: "30px" }}>
            <CircularProgress size={24} />
            <span>Loading job status…</span>
          </div>
        ) : pageItems.length === 0 ? (
          <div id="no-data-msg" className="loading">No jobs found for this selection.</div>
        ) : (
          <table className="gold-table" id="gold-table">
            <thead>
              <tr>
                {visibleColumns.map((c) => (
                  <SortTh key={c.key} columnKey={c.sortKey} sortKey={sortKey} sortDir={sortDir} onSort={onSort}>{c.label}</SortTh>
                ))}
              </tr>
            </thead>
            <tbody id="gold-tbody">
              {pageItems.map((it: any, idx: number) => (
                <tr key={idx} onClick={() => setOpenJob({ ...it, run_date: date })}>
                  {visibleColumns.map((c) => <td key={c.key}>{c.render(it, startIndex + idx)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <Pagination
        total={filteredItems.length}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={(n) => { setRowsPerPage(n); setPage(1); }}
      />
    </div>
  );

  const renderHourlyView = () => (
    <div id="gold-hv-panel">
      <GoldHourlyView
        date={date}
        onDateChange={setDate}
        market={market}
        onMarketChange={setMarket}
        markets={markets}
        onOpenJob={(a) => setOpenJob({ job_name: a.name, schema: a.schema_name || "", run_date: date })}
      />
    </div>
  );

  const renderReportModal = () => (
    <DialogModal
      isOpen={reportOpen}
      handleClose={() => setReportOpen(false)}
      width="lg"
      title={`IBP Gold GDO Report \u2014 ${(reportData && reportData.date) || date}${market ? ` \u00b7 ${market}` : " \u00b7 All Markets"}`}
      headerContent={
        <button onClick={exportCsv} style={{ background: "rgba(255,255,255,0.12)", color: "#fff", border: "1px solid rgba(255,255,255,0.3)", borderRadius: 4, padding: "6px 14px", cursor: "pointer", fontSize: ".8rem", marginTop: 8 }}>&#11015; Download CSV</button>
      }
      dialogContentProps={{ sx: { textAlign: "left" } }}
    >
      <div style={{ padding: "10px 20px", fontSize: ".8rem", color: "#64748b", borderBottom: "1px solid #dde4ec", background: "var(--panel-bg)" }}>
        {reportLoading ? "Loading\u2026" : reportError ? <span style={{ color: "#e74c3c" }}>{reportError}</span> : <><b>Showing:</b> {reportRows.length} jobs</>}
      </div>
      <div style={{ overflowX: "auto", maxHeight: "60vh", overflowY: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: ".8rem", background: "var(--panel-bg)" }}>
          <thead style={{ position: "sticky", top: 0, background: "var(--navy-800)" }}>
            <tr>
              {["Market", "Schema", "Job Name", "GDO#", "FS61 / Exp Start", "FS61 / SLA End", "Actual Start (UTC)", "Actual End (UTC)", "Duration", "Status", "Trigger", "Error"].map((h) => (
                <th key={h} style={{ padding: "8px 10px", textAlign: "left", color: "#ffffff", borderBottom: "1px solid #dde4ec" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            <ReportTableRows reportLoading={reportLoading} reportRows={reportRows} />
          </tbody>
        </table>
      </div>
    </DialogModal>
  );

  return (
    <>
      <Header
        dbLive={view ? view.db_live : undefined}
        right={
          <div className="refresh-info">
            <span id="last-refresh">{view ? `Refreshed ${new Date(view.last_refresh || "").toISOString().slice(11, 19)} UTC` : "Loading\u2026"}</span>
          </div>
        }
      />
      <Nav />

      <div className="summary-bar" id="gold-summary">
        <SummaryCard status="" label="All Jobs" count={summary.total || 0} isActive={statusFilter === ""} onClick={() => toggleCardFilter("")} isLoading={!view} />
        <div className="summary-card-group">
          <SummaryCard status="completed" label="Completed" count={completedCount} isActive={statusFilter === "completed"} onClick={() => toggleCardFilter("completed")} isLoading={!view} />
          <div className="summary-card-sub-stack">
            {subCards.map((c) => (
              <SummaryCard key={c.status} status={c.status} label={c.label} count={c.count} isActive={statusFilter === c.status} onClick={() => toggleCardFilter(c.status)} isLoading={!view} />
            ))}
          </div>
        </div>
        <SummaryCard status="inprog" label="In Progress" count={summary.inprog || 0} isActive={statusFilter === "inprog"} onClick={() => toggleCardFilter("inprog")} isLoading={!view} />
        <SummaryCard status="missed" label="Missed" count={summary.missed || 0} isActive={statusFilter === "missed"} onClick={() => toggleCardFilter("missed")} isLoading={!view} />
        <SummaryCard status="failed" label="Failed" count={summary.failed || 0} isActive={statusFilter === "failed"} onClick={() => toggleCardFilter("failed")} isLoading={!view} />
        <SummaryCard status="scheduled" label="Scheduled" count={summary.scheduled || 0} isActive={statusFilter === "scheduled"} onClick={() => toggleCardFilter("scheduled")} isLoading={!view} />
      </div>

      <div className="view-mode-bar">
        <button className={`view-tab ${viewTab === "table" ? "active" : ""}`} onClick={() => setViewTab("table")}>&#9776; Table View</button>
        <button className={`view-tab ${viewTab === "hourly" ? "active" : ""}`} onClick={() => setViewTab("hourly")}>&#9200; Hourly View</button>
      </div>

      {viewTab === "hourly" ? renderHourlyView() : renderTableView()}

      {openJob && <GoldJobDetail job={openJob} onClose={() => setOpenJob(null)} />}

      {renderReportModal()}
    </>
  );
}
